import { BOOLEAN_RADIO_INPUT_OPTIONS } from "@constants/boolean-radio-input-options";
import { resources } from "@constants/hardcoded-strings";
import { LANGUAGE_SELECT_OPTIONS } from "@constants/language-select-options";
import { ContentPageBody } from "@containers/common/content-page-body/content-page-body";
import { ContentPageHeader } from "@containers/common/content-page-header/content-page-header";
import { MultiSelectFormGroup } from "@containers/common/multi-select-form-group/multi-select-form-group";
import { PropertiesSectionDescription } from "@containers/common/properties-sections/properties-section-header/properties-section-description";
import { RadioInputFormGroup } from "@containers/common/radio-input-form-group/radio-input-form-group";
import { useModalContext } from "@context/use-modal-context";
import { LabelWidthTypes } from "@enums/label-width-types";
import { ModalTypes } from "@enums/modal-types";
import { LocaleTypes } from "@iddataweb/idw-common-ui/enums";
import { AXNUIContent, AXNUILayout, DCUIContent, MultiLanguageWrapper, SelectOption } from "@iddataweb/idw-common-ui/types";
import { isStringTrue } from "@iddataweb/idw-common-ui/utils";
import { WorkflowGeneralTranslationValidationErrors } from "@idw-types/common/validation/workflow-details-validation-errors";
import { FormattedIdwProfile } from "@idw-types/data/idw-profile";
import { AxnDocumentationLink } from "@idw-types/data/reference-data/axn/axn-documentation-link";
import { setAXNUIAndDCUIContentProfilePropertyValue } from "@utils/content-util";
import { useMemo, useState } from "react";
import Alert from "react-bootstrap/Alert";
import { ConfirmModal } from "./confirm-modal/confirm-modal";
import { SortableLanguagesContainer } from "@containers/workflow-details/workflow-details-component/workflow-details-general-tab/translation-tab-container/sortable-languages-container/sortable-languages-container";
import { toObject } from "@utils/object-util";
// import { AXNUI_LAYOUT_PROPERTY_TYPE_ID } from "@constants/verification-service-details-constants";

type TranslationTabContainerProps = {
    isDisabled: boolean;
    axnDocumentationLinks: Array<AxnDocumentationLink>;
    workflowProfile: FormattedIdwProfile;
    multiLanguageAXNUIContent: MultiLanguageWrapper<AXNUIContent>;
    multiLanguageDCUIContent: MultiLanguageWrapper<DCUIContent>;
    availableLanguages: LocaleTypes[];
    isMultiLanguageEnabled: boolean;
    validationErrors: WorkflowGeneralTranslationValidationErrors;
    axnuiLayout: AXNUILayout;
    setAvailableLanguages: (availableLanguages: Array<LocaleTypes>) => void;
    setWorkflowProfile: (workflowProfile: FormattedIdwProfile) => void;
    setIsMultiLanguageEnabled: (isEnabled: boolean) => void;
    updateRpServicesAXNUIAndDCUIContentProperty: (availableLanguages: LocaleTypes[], isMultiLanguageEnabled: boolean) => void;
}

export const TranslationTabContainer = (props: TranslationTabContainerProps) => {
    const { isDisabled, multiLanguageAXNUIContent, multiLanguageDCUIContent,
        availableLanguages, isMultiLanguageEnabled, workflowProfile,
        validationErrors, axnDocumentationLinks, setAvailableLanguages,
        setWorkflowProfile, setIsMultiLanguageEnabled, updateRpServicesAXNUIAndDCUIContentProperty
    } = props;

    const [labelWidth] = useState(LabelWidthTypes.LARGE);
    const modalContext = useModalContext();

    const handleAvailableLanguageChange = (options: Array<SelectOption<LocaleTypes>>) => {
        const newAvailableLanguages = options.map(option => option.value);

        if (availableLanguages.length > newAvailableLanguages.length) {
            const removedLanguage = availableLanguages.find(lng => !newAvailableLanguages.includes(lng));
            const removedLanguageOption = LANGUAGE_SELECT_OPTIONS.find(option => option.value === removedLanguage);

            const confirmMessage = getWarningMessageElement(removedLanguageOption.label);
            renderConfirmLanguageDeletionModal(confirmMessage, newAvailableLanguages, onSubmit);

        } else {
            onSubmit(newAvailableLanguages);
        }
    }

    const updateAXNUIAndDCUIContentPropertyValue = (availableLanguages: LocaleTypes[], isMultiLanguageEnabled: boolean, shouldEnableLocalizeString: boolean) => {
        const newProperties = setAXNUIAndDCUIContentProfilePropertyValue(workflowProfile, isMultiLanguageEnabled, multiLanguageAXNUIContent, multiLanguageDCUIContent,
            availableLanguages, null, shouldEnableLocalizeString);


        setWorkflowProfile({
            ...workflowProfile,
            profileProperties: newProperties
        });
    };

    const onSubmit = (newAvailableLanguages: Array<LocaleTypes>) => {
        setAvailableLanguages(newAvailableLanguages);
        updateAXNUIAndDCUIContentPropertyValue(newAvailableLanguages, isMultiLanguageEnabled, false);
        updateRpServicesAXNUIAndDCUIContentProperty(newAvailableLanguages, true);
    }

    const getWarningMessageElement = (languageLabel: string) => {
        return <Alert variant="warning">
            {
                translationResources.inputs.languages.confirmLanguageDeletion.warningMessage(languageLabel)
            }
        </Alert>;
    }

    const handleToggleTranslationChange = (value: string) => {
        const isEnabled = isStringTrue(value);
        let newAvailableLanguages: Array<LocaleTypes> = [];

        if (!isEnabled) {
            updateAXNUIAndDCUIContentPropertyValue([], false, false);
        } else {
            updateAXNUIAndDCUIContentPropertyValue([LocaleTypes.ENGLISH], true, true);
            newAvailableLanguages = [LocaleTypes.ENGLISH]
        }

        setAvailableLanguages(newAvailableLanguages);
        updateRpServicesAXNUIAndDCUIContentProperty(newAvailableLanguages, isEnabled);
        setIsMultiLanguageEnabled(isEnabled);
    }

    const handleUpdateLanguagesPositions = (availableLanguages: Array<SelectOption<LocaleTypes>>) => {
        const updatedAvailableLanguages = availableLanguages.map(option => option.value);
        onSubmit(updatedAvailableLanguages);
    }

    // const handleToggleTranslationDefaultUiPrimaryLanguageChange = (value: string) => {
    //     const newProperties = workflowProfile.profileProperties.map(property => {
    //         if (property.availableProperty?.id === AXNUI_LAYOUT_PROPERTY_TYPE_ID) {
    //             return {
    //                 ...property,
    //                 value: JSON.stringify({
    //                     ...axnuiLayout,
    //                     usePrimaryLanguageByDefault: isStringTrue(value)
    //                 })
    //             };
    //         }

    //         return property;
    //     });


    //     setWorkflowProfile({
    //         ...workflowProfile,
    //         profileProperties: newProperties
    //     });
    // }

    const languageSelectOptionsMap = useMemo(() => {
        return toObject<SelectOption<LocaleTypes>>(LANGUAGE_SELECT_OPTIONS, 'value');
    }, []);

    const languageOptions = useMemo(() => {
        return availableLanguages.map(lng => {
            return languageSelectOptionsMap[lng];
        });

    }, [availableLanguages]);

    const translationResources = resources.pages.workflowDetails.tabs.general.subTabs.translation;

    const renderConfirmLanguageDeletionModal = (
        confirmMessage: string | React.ReactNode,
        newAvailableLanguages: Array<LocaleTypes>,
        onSubmit: (newAvailableLanguages: Array<LocaleTypes>) => void) => {

        const renderModal = (onModalClose: () => void) => {
            return (
                <ConfirmModal
                    id={"confirm-language-unselect"}
                    title={translationResources.inputs.languages.confirmLanguageDeletion.title}
                    confirmMessage={confirmMessage}
                    cancelButtonText={resources.common.buttons.cancel.label}
                    submitButtonText={translationResources.inputs.languages.confirmLanguageDeletion.confirmButtonText}
                    onSubmit={onSubmit}
                    onSubmitAsync={null}
                    onModalClose={onModalClose}
                    customProps={newAvailableLanguages}
                />
            );
        };

        modalContext.setModalState({
            modalType: ModalTypes.CONFIRM,
            renderFunction: renderModal
        });
    };

    const documentationLink = useMemo(() => {
        if (axnDocumentationLinks.length) {
            const documentationLink = axnDocumentationLinks.find(doc => doc.shortName === 'translation');
            if (documentationLink) {
                return documentationLink.url;
            }
        }

        return '';
    }, [axnDocumentationLinks]);


    return <div>
        <ContentPageHeader title={translationResources.title} />
        <ContentPageBody isMaxWidth={true}>
            <PropertiesSectionDescription description={translationResources.description} documentationLink={documentationLink} />
            <RadioInputFormGroup
                controlId={'translation-enable-transaction-radio-button'}
                automationId={'translation-enable-transaction-radio-button'}
                disabled={isDisabled}
                isRequired={false}
                label={translationResources.inputs.enableTranslation.label}
                labelWidth={LabelWidthTypes.LARGE}
                value={isMultiLanguageEnabled
                    ? BOOLEAN_RADIO_INPUT_OPTIONS[0].value
                    : BOOLEAN_RADIO_INPUT_OPTIONS[1].value
                }
                options={BOOLEAN_RADIO_INPUT_OPTIONS}
                onChange={handleToggleTranslationChange}
            />


            {isMultiLanguageEnabled && (
                <>
                    <MultiSelectFormGroup
                        label={translationResources.inputs.languages.label}
                        ariaLabel={translationResources.inputs.languages.label}
                        automationId={"transaction-languages-multi-select"}
                        controlId={"transaction-languages-multi-select"}
                        disabled={isDisabled}
                        isClearable={false}
                        isRequired={false}
                        isSearchable={true}
                        useDefaultValueContainer={true}
                        labelWidth={labelWidth}
                        onChangeMulti={handleAvailableLanguageChange}
                        options={LANGUAGE_SELECT_OPTIONS}
                        selectedOptions={languageOptions}
                        keepMenuOpenOnSelect={true}
                    />
                    {validationErrors.availableLanguagesError}

                    <div>
                        <ContentPageHeader title={translationResources.inputs.languagesOrder.title} />
                        <ContentPageBody isMaxWidth={true}>
                            <PropertiesSectionDescription description={translationResources.inputs.languagesOrder.description} />
                            {/* <RadioInputFormGroup
                                controlId={'translation-default-ui-to-primary-language-radio-button'}
                                automationId={'translation-default-ui-to-primary-language-radio-button'}
                                disabled={isDisabled}
                                isRequired={false}
                                label={translationResources.inputs.usePrimaryLanguageByDefault.label}
                                labelWidth={LabelWidthTypes.LARGE}
                                value={axnuiLayout.usePrimaryLanguageByDefault
                                    ? BOOLEAN_RADIO_INPUT_OPTIONS[0].value
                                    : BOOLEAN_RADIO_INPUT_OPTIONS[1].value
                                }
                                options={BOOLEAN_RADIO_INPUT_OPTIONS}
                                onChange={handleToggleTranslationDefaultUiPrimaryLanguageChange}
                                afterLabelTooltipText={translationResources.inputs.usePrimaryLanguageByDefault.tooltip}
                            /> */}

                            <SortableLanguagesContainer
                                isDisabled={isDisabled}
                                selectedLanguages={languageOptions}
                                updateLanguagesPositions={handleUpdateLanguagesPositions}
                            />
                        </ContentPageBody>
                    </div>


                </>
            )}
        </ContentPageBody>
    </div>
}